/**
 * PrepChecklist — 10-item hurricane prep checklist.
 * State persisted in localStorage per personaId so P1 and P3 keep
 * independent progress across switches.
 * Atlas: E5.
 */
"use client";

import { useState, useEffect } from "react";
import { CheckCircle2, Circle } from "lucide-react";
import { PREP_CHECKLIST } from "@petci/fixtures";
import type { PersonaId } from "@petci/fixtures";
import { useT } from "@/lib/locale";

function storageKey(personaId: PersonaId) {
  return `petci_hurricane_prep_${personaId}`;
}

function loadChecked(personaId: PersonaId): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(storageKey(personaId));
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function saveChecked(personaId: PersonaId, checked: Set<string>) {
  if (typeof window === "undefined") return;
  localStorage.setItem(storageKey(personaId), JSON.stringify([...checked]));
}

interface Props {
  personaId: PersonaId;
}

export function PrepChecklist({ personaId }: Props) {
  const t = useT();
  const [checked, setChecked] = useState<Set<string>>(new Set());

  useEffect(() => {
    setChecked(loadChecked(personaId));
  }, [personaId]);

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveChecked(personaId, next);
      return next;
    });
  }

  const done = checked.size;
  const total = PREP_CHECKLIST.length;

  return (
    <div className="card-surface space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-neutral-700">{t("hurricane.prepChecklistTitle")}</h2>
        <span className="text-xs text-neutral-700">
          {t("hurricane.checklistProgress", { done, total })}
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-neutral-100 rounded-full h-1.5">
        <div
          className="bg-semantic-warning h-1.5 rounded-full transition-all duration-300"
          style={{ width: `${(done / total) * 100}%` }}
        />
      </div>

      <ul className="space-y-2">
        {PREP_CHECKLIST.map((item) => {
          const isChecked = checked.has(item.id);
          return (
            <li key={item.id}>
              <button
                onClick={() => toggle(item.id)}
                /* R-A: the 7 items without a detail line measured 20px tall —
                   under the 24px effective-target floor. `py-1 -my-1` lifts the
                   hit box to 28px and hands the space back, so the list's own
                   rhythm is byte-identical. */
                className="w-full flex items-start gap-3 text-left group py-1 -my-1"
                aria-pressed={isChecked}
                aria-label={t(item.labelKey)}
              >
                <span className="mt-0.5 flex-shrink-0 text-semantic-warning-text group-hover:text-semantic-warning-text/80 transition-colors duration-fast">
                  {isChecked ? (
                    <CheckCircle2 size={18} className="text-semantic-success-text" aria-hidden />
                  ) : (
                    <Circle size={18} aria-hidden />
                  )}
                </span>
                <div className="flex-1 min-w-0">
                  <p
                    className={[
                      "text-sm",
                      isChecked ? "line-through text-neutral-500" : "text-neutral-800",
                    ].join(" ")}
                  >
                    {t(item.labelKey)}
                  </p>
                  {item.detailKey && (
                    <p className="text-xs text-neutral-700 mt-0.5">{t(item.detailKey)}</p>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
